import React, { useContext, useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  TextField,
  CircularProgress,
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  Grid,
  Divider,
  Snackbar,
  Tabs,
  Tab,
  Box
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import Button from 'components/common/Button'
import ErrorDialog from 'components/common/ErrorDialog'
import GridHeader from 'components/common/GridHeader'
import PendingBOLsList from 'components/BOL/PendingBOLsList'
import { queries } from 'queries'
import { commas, noCommas, useForm, useGqlDirect, useRestApiPost } from 'helpers'
import { useSSE } from 'hooks/useSSE'
import { useConfig } from 'hooks/useConfig'
import { ReducerContext } from 'App'
import axios from 'axios'
import { isEthanolTerminal, SAND_PRODUCTS, CEMENT_PRODUCTS, ETHANOL_PRODUCTS } from 'constants/productTypes'

const PRODUCT_CATEGORIES = {
  SAND: { label: 'Sand', products: SAND_PRODUCTS },
  CEMENT: { label: 'Cement', products: CEMENT_PRODUCTS },
  ETHANOL: { label: 'Ethanol', products: ETHANOL_PRODUCTS }
}

const init = {
  po_id: '',
  sand_type: '',
  buyer: '',
  transloading_client: '',
  job_name: '',
  external_reference_number: '',
  driver_name: '',
  truck_number: '',
  trailer_number: '',
  carrier: '',
  tare_weight: '',
  notes: '',
  truck_load_id: ''
}

const BOLCreate = () => {
  const {
    state: {
      user: { id: dbUserId },
      currentTerminal
    }
  } = useContext(ReducerContext)
  const { config } = useConfig()
  const history = useHistory()

  // For tracking pending BOLs
  const [pendingBOLs, setPendingBOLs] = useState([])
  const [selectedBOL, setSelectedBOL] = useState(null)
  const [selectedBOLId, setSelectedBOLId] = useState(null)
  const [acknowledgedBOLIds, setAcknowledgedBOLIds] = useState(() => {
    const saved = localStorage.getItem('acknowledgedBOLIds')
    return saved ? JSON.parse(saved) : []
  })
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' })
  const [activeTab, setActiveTab] = useState(0)

  // Tab configuration for multi-product terminals
  const showTabs = isEthanolTerminal(currentTerminal)
  const availableTabs = showTabs
    ? [PRODUCT_CATEGORIES.SAND, PRODUCT_CATEGORIES.CEMENT, PRODUCT_CATEGORIES.ETHANOL]
    : [PRODUCT_CATEGORIES.SAND]
  const currentCategory = availableTabs[activeTab]

  const previousBOLIdsRef = useRef(new Set())
  const [newBOLHighlight, setNewBOLHighlight] = useState(null)

  const handleAcknowledgeBOL = bolId => {
    const updatedIds = [...acknowledgedBOLIds, bolId]
    setAcknowledgedBOLIds(updatedIds)
    localStorage.setItem('acknowledgedBOLIds', JSON.stringify(updatedIds))
  }

  const handleDismissBOL = async (bol) => {
    try {
      // Update BOL status to 'user_dismissed'
      await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
        array: [{
          bolId: bol.bolId,
          status: 'user_dismissed',
          notes: `${bol.notes || ''}\n[Dismissed by user at ${new Date().toISOString()}]`
        }],
        dbTable: 'Bills_Of_Lading',
        dbEvent: 'update',
        dbUnique: 'bolId',
        dbUserId: dbUserId
      })

      // Refresh the pending BOLs list by calling the API directly
      const response = await axios.post(`${config.LOGITRAC_API_URL}api/pending_bols`, {
        currentTerminal,
        status: ['pending_completion', 'sanddrive_missing_po', 'sanddrive_canceled', 'sanddrive_change_pull_point']
      })
      updatePendingBOLsWithHighlight(response.data)

      // Show success message
      alert(`BOL #${bol.bolId} has been dismissed`)
    } catch (error) {
      console.error('Failed to dismiss BOL:', error)
      alert('Failed to dismiss BOL')
    }
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  const updatePendingBOLsWithHighlight = useCallback(bolsData => {
    if (!bolsData) return
    
    const currentBOLIds = new Set(bolsData.map(bol => bol.bolId))
    const newBOLs = bolsData.filter(bol => !previousBOLIdsRef.current.has(bol.bolId))

    if (newBOLs.length > 0 && previousBOLIdsRef.current.size > 0) {
      setNewBOLHighlight(newBOLs[0].bolId)
      setTimeout(() => setNewBOLHighlight(null), 3000)
    }

    previousBOLIdsRef.current = currentBOLIds
    setPendingBOLs(bolsData)
  }, [])

  const fetchPendingBOLs = useCallback(async () => {
    try {
      const response = await axios.post(`${config.LOGITRAC_API_URL}api/pending_bols`, {
        currentTerminal,
        status: ['pending_completion', 'sanddrive_missing_po', 'sanddrive_canceled', 'sanddrive_change_pull_point']
      })
      
      updatePendingBOLsWithHighlight(response.data)
    } catch (error) {
      console.error('Error fetching pending BOLs:', error)
    }
  }, [config.LOGITRAC_API_URL, currentTerminal, updatePendingBOLsWithHighlight])


  const { setValue, values, resetValues } = useForm(init)

  const { loading, error, data } = useGqlDirect(queries.GET_ACTIVE_PO_CREATE_BOL)

  const requestBody = useMemo(
    () => ({
      currentTerminal,
      status: ['pending_completion', 'sanddrive_missing_po', 'sanddrive_canceled', 'sanddrive_change_pull_point']
    }),
    [currentTerminal]
  )

  const {
    data: pendingBOLsData,
    loading: pendingBOLsLoading,
    error: pendingBOLsError
  } = useRestApiPost(`api/pending_bols`, requestBody)

  // Connect to SSE for real-time updates
  const sseEndpoint = `${config.LOGITRAC_API_URL}api/bol-events`
  const { data: sseData, clearData } = useSSE(sseEndpoint)

  useEffect(() => {
    updatePendingBOLsWithHighlight(pendingBOLsData)
  }, [pendingBOLsData, updatePendingBOLsWithHighlight])

  useEffect(() => {
    if (sseData && (sseData.type === 'pending_bol_change' || sseData.type === 'pending_bol_error')) {
      fetchPendingBOLs()

      if (sseData.action === 'created') {
        console.log('New pending BOL created for fulfillment:', sseData.source_fulfillment_id)
      } else if (sseData.action === 'missing_po') {
        console.log('BOL created with missing PO:', sseData.missing_po, 'for fulfillment:', sseData.source_fulfillment_id)
        setNotification({
          open: true,
          message: `BOL created but PO '${sseData.missing_po}' not found. Please create the PO in Logitrac.`,
          severity: 'warning'
        })
      } else if (sseData.action === 'canceled') {
        console.log('BOL canceled for fulfillment:', sseData.source_fulfillment_id, 'Reason:', sseData.reason)

        // Check if the cancelled BOL was currently selected
        if (selectedBOL && selectedBOL.source_fulfillment_id === sseData.source_fulfillment_id) {
          // Clear the form
          setSelectedBOL(null)
          setSelectedBOLId(null)
          resetValues()

          // Show notification based on reason
          let message = 'The selected BOL has been cancelled. Please select a different BOL.'
          if (sseData.reason === 'change_pull_point') {
            message = 'The selected BOL has been rerouted to a different pull point. Please select the new BOL if available.'
          }

          setNotification({
            open: true,
            message: message,
            severity: 'warning'
          })
        }
      }

      clearData()
    }
  }, [sseData, clearData, fetchPendingBOLs, selectedBOL, resetValues])

  const handlePOSelect = e => {
    // Clear any selected pending BOL
    setSelectedBOL(null)
    setSelectedBOLId(null)

    const [selectedPO] = data.pos.filter(po => po.id === e.target.value && po.terminal === currentTerminal)
    const { id, buyer, sand_type, transloading_client, external_reference_number, job_name } = selectedPO
    Object.entries({ po_id: id, buyer, sand_type, transloading_client, external_reference_number, job_name }).map(
      ([key, val]) => setValue(key, val)
    )

    return null // React expects return value from map
  }

  const handleSelectPendingBOL = bol => {
    setSelectedBOL(bol)
    setSelectedBOLId(bol.bolId)

    // Fill the form with the pending BOL data
    resetValues() // Clear form first

    const { po_id, driver_name, truck_number, trailer_number, carrier, tare_weight, notes, truck_load_id } = bol

    const { sand_type, buyer, transloading_client, job_name, external_reference_number } = bol.Purchase_Orders || {}

    // Set all the form values
    Object.entries({
      po_id,
      driver_name,
      truck_number,
      trailer_number,
      carrier,
      tare_weight: commas(tare_weight || ''), // Format with commas
      notes: notes || '',
      sand_type,
      buyer,
      transloading_client,
      job_name,
      external_reference_number,
      truck_load_id: truck_load_id || ''
    }).forEach(([key, val]) => setValue(key, val))
  }

  const handleCancel = () => {
    // Clear selected BOL
    setSelectedBOL(null)
    setSelectedBOLId(null)
    resetValues()
  }

  const handleForm = async e => {
    e.preventDefault()
    const { tare_weight, buyer, sand_type, transloading_client, external_reference_number, job_name, truck_load_id, ...rest } = values

    // Prepare the common BOL data
    const bolData = {
      ...rest,
      tare_weight: noCommas(tare_weight),
      user: dbUserId,
      terminal: currentTerminal,
      ...(truck_load_id && { truck_load_id })
    }

    try {
      if (selectedBOL) {
        // If we're completing a pending BOL, update it instead of creating a new one
        await axios.post(`${config.LOGITRAC_API_URL}api/update`, {
          array: [
            {
              ...bolData,
              bolId: selectedBOL.bolId,
              status: 'started' // Change from pending_completion to started
            }
          ],
          dbTable: 'Bills_Of_Lading',
          dbEvent: 'update',
          dbUnique: 'bolId',
          dbUserId
        })
      } else {
        // Create a new BOL as before
        await axios.post(`${config.LOGITRAC_API_URL}api/insert`, {
          item: bolData,
          dbTable: 'Bills_Of_Lading',
          dbEvent: 'insert',
          dbUserId
        })
      }

      // Reset state and redirect
      setSelectedBOL(null)
      setSelectedBOLId(null)
      history.push('/outbound')
    } catch (error) {
      console.log('error: ', error)
    }
  }

  const textFields = [
    { disabled: true, width: 6, id: 'sand_type', label: 'Product' },
    { disabled: true, width: 6, id: 'buyer', label: 'Buyer' },
    { disabled: true, width: 6, id: 'transloading_client', label: 'Transloading Client' },
    { disabled: true, width: 6, id: 'job_name', label: 'Job Name' },
    { disabled: true, width: 6, id: 'external_reference_number', label: 'External Reference Number' },
    ...(values.truck_load_id ? [{ disabled: true, width: 6, id: 'truck_load_id', label: 'SandDrive Truck Load ID' }] : []),
    { disabled: false, width: 12, id: 'driver_name', label: "Driver's Full Name" },
    { disabled: false, width: 6, id: 'truck_number', label: 'Truck Number' },
    { disabled: false, width: 6, id: 'trailer_number', label: 'Trailer Number' },
    { disabled: false, width: 12, id: 'carrier', label: 'Carrier Name' },
    {
      disabled: false,
      width: 12,
      id: 'tare_weight',
      label: 'Tare Weight',
      onChange: e => setValue(e.target.id, commas(e.target.value)),
      error: noCommas(values['tare_weight']) < 15000 || noCommas(values['tare_weight']) > 80000,
      helperText:
        noCommas(values['tare_weight']) < 15000
          ? 'Tare Weight must be greater than 15,000 lbs'
          : noCommas(values['tare_weight']) > 80000
          ? 'Tare Weight must be less than 80,000 lbs'
          : ''
    },
    { disabled: false, width: 12, id: 'notes', label: 'Notes' }
  ]

  const makeField = ({ id, label, width, disabled, onChange, error, helperText }) => (
    <Grid item xs={12} sm={width} md={width} className="GridItem" key={id}>
      <TextField
        id={id}
        label={label}
        value={values[id]}
        onChange={onChange ? onChange : e => setValue(e.target.id, e.target.value)}
        margin="normal"
        fullWidth
        required={id !== 'notes'}
        error={error}
        helperText={helperText}
        disabled={disabled}
      />
    </Grid>
  )

  return (
    <Grid container className="GridContainer">
      <GridHeader
        h4="Start New BOL"
        p="Complete all details below or select a pending BOL from SandDrive below to complete."
      >
        {loading && <CircularProgress />}
        {error && <ErrorDialog error={error} />}
        {data && (
          <>
            {showTabs && (
              <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) => {
                    setActiveTab(newValue)
                    resetValues()
                    setSelectedBOL(null)
                    setSelectedBOLId(null)
                  }}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {availableTabs.map(tab => (
                    <Tab key={tab.label} label={tab.label} />
                  ))}
                </Tabs>
              </Box>
            )}
            <form onSubmit={handleForm}>
              <Grid container className="GridContainer">
                <Grid item xs={12} sm={6} md={6} className="GridItem">
                  <FormControl fullWidth required style={{ margin: '16px 0 8px' }}>
                    <InputLabel>Purchase Order Number</InputLabel>
                    <Select
                      name="po_id"
                      onChange={handlePOSelect}
                      fullWidth
                      required
                      value={values['po_id']}
                      disabled={!!selectedBOL}
                    >
                      {data.pos
                        .filter(po => po.terminal === currentTerminal && currentCategory.products.some(p => p === po.sand_type))
                        .map(PurchaseOrder => (
                          <MenuItem key={PurchaseOrder.id} value={PurchaseOrder.id}>
                            {PurchaseOrder.purchase_order_number}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>

                {textFields.map(field => makeField(field))}
              </Grid>

              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    color="primary"
                    type="submit"
                    value="submit"
                    disabled={noCommas(values['tare_weight']) < 15000 || noCommas(values['tare_weight']) > 80000}
                  >
                    {selectedBOL ? 'Complete Pending BOL' : 'Submit BOL'}
                  </Button>
                </Grid>
                {selectedBOL && (
                  <Grid item>
                    <Button color="default" onClick={handleCancel}>
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </form>

            <Divider style={{ margin: '20px 0' }} />

            {/* Pending BOLs List */}
            {pendingBOLsLoading ? (
              <CircularProgress />
            ) : pendingBOLsError ? (
              <ErrorDialog error={'Error loading pending BOLs'} />
            ) : (
              <PendingBOLsList
                pendingBOLs={pendingBOLs}
                onSelectBOL={handleSelectPendingBOL}
                selectedBOLId={selectedBOLId}
                newBOLHighlight={newBOLHighlight}
                acknowledgedBOLIds={acknowledgedBOLIds}
                onAcknowledgeBOL={handleAcknowledgeBOL}
                onDismissBOL={handleDismissBOL}
                cancellationTimeThreshold={24} // 24 hours - can be changed as needed
              />
            )}
          </>
        )}
      </GridHeader>

      <Snackbar
        open={notification.open}
        autoHideDuration={8000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default BOLCreate
